import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  FormField,
  Grid,
  Select,
  SelectProps,
  SpaceBetween,
  Spinner } from '@amzn/awsui-components-react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectEmployeeId,
  selectSideTeam,
  selectSigAdmin,
  selectUserPrefs,
  selectUserSites,
  selectUsername,
  setUserPrefs,
  setUserSelectedSite,
  setUserSelectedSitePrivs} from '../stores/slices/userSlice';
import { fetchUserSitePrivs, Privileges } from 'src/utils/UserSitesUtils';
import { updateUserPrefs } from 'src/utils/UserPrefsUtils';
import { createUserAction } from 'src/utils/UserActionsUtils';
import { UserActionNames } from 'src/constants/Constants';
import * as APIt from '../API';
import {
  setSelectedInputs,
  setSelectedReadersAlarms,
  setSelectedReadersModes} from '../stores/slices/devicesSlice';
import { debug } from '../utils/commonUtils';

export default function SelectSite(props: any) {
  debug(`SelectSite(): props is ${JSON.stringify(props)}`);

  const dispatch = useDispatch();

  const employeeId = useSelector(selectEmployeeId) as number;
  const sideTeam = useSelector(selectSideTeam) as string;
  const sigAdmin = useSelector(selectSigAdmin) as string;
  const username = useSelector(selectUsername) as string;
  const userPrefs = useSelector(selectUserPrefs) as APIt.UserPrefs;
  const userSites = useSelector(selectUserSites) as APIt.UserPrefSite[];

  const [siteRequiredAlertVisible, setSiteRequiredAlertVisible] = useState<boolean>(false);
  const [selectedSiteName, setSelectedSiteName] = React.useState<string | undefined>(props.selectedSite?.sitename);
  debug(`SelectSite(): selectedSiteName is ${selectedSiteName}`);

  const siteFieldOnChangeHandler = (detail: SelectProps.ChangeDetail) => {
    setSelectedSiteName(detail.selectedOption.label);
  };

  const siteSelected = async () => {
    debug(`SelectSite(): siteSelected() selectedSiteName is ${selectedSiteName}`);
    const selectedSite = userSites.find(s => s.sitename === selectedSiteName);
    createUserAction(
      {
        actionName: UserActionNames.SelectSite,
        username: username,
        parameters: JSON.stringify(
          {
            selectedSite: selectedSiteName,
            employeeId: employeeId
          })
      });
    debug(`SelectSite(): siteSelected() selectedSite is ${JSON.stringify(selectedSite)}`);
    if (!selectedSite || !selectedSite.sitename) {
      setSiteRequiredAlertVisible(true);
      return;
    }
    dispatch(setUserSelectedSitePrivs(undefined));
    dispatch(setUserSelectedSite(selectedSite));
    debug(`SelectSite(): siteSelected() calling dispatch(setSelectedInputs())`);
    dispatch(setSelectedInputs([]));
    dispatch(setSelectedReadersAlarms([]));
    dispatch(setSelectedReadersModes([]));
    if (props.setSiteSelectVisibleCallback) props.setSiteSelectVisibleCallback(false);
    if (props.siteSelectedCallback) props.siteSelectedCallback(true);

    if (sideTeam || sigAdmin) {
      dispatch(setUserSelectedSitePrivs([Privileges.all]));
    } else {
      const userSelectedSitePrivs = await fetchUserSitePrivs(employeeId, username, selectedSite.sitename, 'MASKING APPROVERS');
      debug(`SelectSite(): siteSelected() userSelectedSitePrivs is ${JSON.stringify(userSelectedSitePrivs)}`);
      dispatch(setUserSelectedSitePrivs(userSelectedSitePrivs));
    }

    debug(`SelectSite(): siteSelected() userPrefs is ${JSON.stringify(userPrefs)}`);
    let newUserPrefs = 
      {
        ...userPrefs,
        lastSite: selectedSite
      };
    debug(`SelectSite(): siteSelected() newUserPrefs is ${JSON.stringify(newUserPrefs)}`);
    dispatch(setUserPrefs(newUserPrefs))
    updateUserPrefs(newUserPrefs);
  };

  const refreshUserSites = () => {
    debug('SelectSite(): refreshUserSites()')
    if (props.refreshUserSitesCallback) props.refreshUserSitesCallback();
  };

  const dismiss = () => {
    if (props.setSiteSelectVisibleCallback) props.setSiteSelectVisibleCallback(false);
  };

  const siteSelectRef = useRef<SelectProps.Ref>(null);

  useEffect(() => {
    debug(`SelectSite() useEffect()[userPrefs] userPrefs is ${JSON.stringify(userPrefs)}`);
    siteSelectRef.current?.focus();
    debug(`SelectSite() useEffect()[userPrefs] userSites is ${JSON.stringify(userSites)}`);
    if (userPrefs && userPrefs.lastSite && userSites.some(us => us.sitename === userPrefs.lastSite?.sitename)) {
      debug('SelectSite() useEffect()[userPrefs] setting selectedSiteName');
      setSelectedSiteName(userPrefs.lastSite.sitename);
    }
  }, [userPrefs]);

  return(
    <Container
      footer={
        <Grid
          gridDefinition={[
            { colspan: 12 },
          ]}
        >
        <Box float='right'>
          <SpaceBetween size='m' direction='horizontal'>
            <Button
              variant='primary'
              onClick={siteSelected}
              disabled={!selectedSiteName}
              ariaLabel='Select User Site'
            >
              Select
            </Button>
            <Button
              variant='normal'
              onClick={refreshUserSites}
              ariaLabel='Refresh User Sites'
            >
              <div hidden={!props.loadingSites}>
                <Spinner />
              </div>
              <div hidden={props.loadingSites}>
                Refresh Sites
              </div>
            </Button>
          </SpaceBetween>
        </Box>
        </Grid>
      }
      header='Choose a Site'
    >
      <SpaceBetween size='m' direction='vertical'>
        <FormField label='Site'>
          <Select
            ref={siteSelectRef}
            filteringType='auto'
            loadingText='Loading Sites...'
            empty='No User Sites Found'
            placeholder='Select Site'
            filteringPlaceholder='Enter Text to Search Sites'
            onChange={({ detail }) => siteFieldOnChangeHandler(detail)}
            options={userSites.map(s => { return { label: s.sitename, value: s.sitename }; } ).sort((c,p) => c.label! < p.label! ? -1 : 1)}
            ariaLabel='User Sites'
            selectedAriaLabel='Selected User Site'
            ariaDescribedby={selectedSiteName}
            renderHighlightedAriaLive={option => `Select Site ${option.label}`}
            ariaRequired
            selectedOption={{ label: selectedSiteName, value: selectedSiteName }}
            statusType={props.loadingSites ? 'loading' : 'finished'}
          />
        </FormField>
        <Alert
          dismissAriaLabel='Close alert'
          dismissible
          onDismiss={() => setSiteRequiredAlertVisible(false)}
          header='Selection Required'
          visible={siteRequiredAlertVisible}
        >
          A site must be selected to continue. Press the Select button to confirm your selection.
        </Alert>
      </SpaceBetween>
    </Container>
  );
}